import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2, MapPin, Globe, Plus, ScanSearch, ArrowRight, Loader2, ExternalLink,
  TrendingUp, TrendingDown, Minus, RefreshCw, FileText, History, ShieldCheck,
  ShieldAlert, AlertTriangle, CheckCircle2, Trash2, Sparkles, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, getSavedKeys, removeSavedKey } from "@/lib/api";
import { scoreColor, scoreBand, COMPLIANCE_BAND } from "@/lib/constants";
import { ProvBadge, StalenessPill, VerificationBadge } from "@/lib/provenance";

export default function Businesses() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changeFor, setChangeFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);

  const load = useCallback(async () => {
    // The browser owns the saved list (localStorage). A `?keys=` param can seed/extend it
    // (shareable lists + a clean migration path off localStorage later).
    const fromUrl = (params.get("keys") || "").split(",").map((k) => k.trim()).filter(Boolean);
    const keys = Array.from(new Set([...fromUrl, ...getSavedKeys()]));
    if (!keys.length) { setBusinesses([]); setLoading(false); return; }
    try {
      const { data } = await api.post("/businesses/overview", { keys });
      setBusinesses(data.businesses || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const remove = async (b) => {
    removeSavedKey(b.business_key);
    setBusinesses((l) => l.filter((x) => x.business_key !== b.business_key));
    try { await api.delete(`/saved-businesses/${encodeURIComponent(b.business_key)}`); } catch (e) { /* noop */ }
    toast.success("Removed from My Businesses.");
  };

  const onResolved = (updated) => {
    setBusinesses((l) => l.map((x) => (x.business_key === changeFor?.business_key
      ? { ...x, ...updated } : x)));
    load();
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 border border-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700 mb-3">
              <Building2 className="w-3.5 h-3.5" /> My Businesses
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Your saved businesses</h1>
            <p className="mt-2 text-slate-600 max-w-2xl">Re-scan in seconds, watch for changes to your public identity, and track how each score improves over time.</p>
          </div>
          <Button onClick={() => nav("/scan")} data-testid="businesses-new-scan"
            className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold px-5 active:scale-95 transition-transform">
            <Plus className="w-4 h-4 mr-1.5" /> Scan a Business
          </Button>
        </div>

        {loading ? (
          <div className="grid place-items-center py-24"><Loader2 className="w-7 h-7 text-indigo-600 animate-spin" /></div>
        ) : businesses.length === 0 ? (
          <EmptyState onScan={() => nav("/scan")} />
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="businesses-grid">
            {businesses.map((b, i) => (
              <BusinessCard key={b.business_key} b={b} index={i}
                onRescan={() => nav(`/scan?business=${encodeURIComponent(b.business_key)}`)}
                onCheck={() => setChangeFor(b)}
                onHistory={() => setHistoryFor(b)}
                onReport={() => b.last_scan?.id && nav(`/report/${b.last_scan.id}`)}
                onRemove={() => remove(b)} />
            ))}
          </div>
        )}
      </div>

      <ChangeDialog business={changeFor} onClose={() => setChangeFor(null)} onResolved={onResolved}
        onRediscover={(b) => nav(`/scan?business=${encodeURIComponent(b.business_key)}`)} />
      <HistoryDialog business={historyFor} onClose={() => setHistoryFor(null)} />
    </div>
  );
}

function EmptyState({ onScan }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-14 text-center" data-testid="businesses-empty">
      <span className="grid place-items-center w-14 h-14 rounded-xl bg-slate-900 text-white mx-auto mb-5"><Building2 className="w-7 h-7" /></span>
      <h3 className="font-heading text-xl font-semibold text-slate-900">No saved businesses yet</h3>
      <p className="mt-2 text-slate-500 max-w-md mx-auto">Run a scan, confirm the business identity, then tap “Save for next time”. Saved businesses appear here for one-tap re-scans and change checks.</p>
      <Button onClick={onScan} data-testid="businesses-empty-scan"
        className="mt-6 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 active:scale-95 transition-transform">
        Scan Your First Business <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
}

function BusinessCard({ b, index, onRescan, onCheck, onHistory, onReport, onRemove }) {
  const ls = b.last_scan;
  const trend = b.score_trend || {};
  const comp = ls?.compliance;
  const loc = [b.city, b.state].filter(Boolean).join(", ");
  const webUrl = b.website;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      data-testid={`business-card-${b.business_key}`}
      className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-slate-900 text-white shrink-0"><Building2 className="w-5 h-5" /></span>
          <div className="min-w-0">
            <p className="font-heading font-bold text-slate-900 truncate" data-testid={`business-name-${b.business_key}`}>{b.name}</p>
            <p className="text-xs text-slate-500 truncate">{b.category_display || b.category || "Business"}{loc ? ` · ${loc}` : ""}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <VerificationBadge verification={b.verification} testid={`business-verify-${b.business_key}`} />
        </div>
      </div>

      {b.description && <p className="mt-3 text-sm text-slate-600 line-clamp-2">{b.description}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
        {webUrl ? (
          <a href={webUrl.startsWith("http") ? webUrl : `https://${webUrl}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-indigo-600 hover:underline break-all"><Globe className="w-3.5 h-3.5" /> {webUrl} <ExternalLink className="w-3 h-3" /></a>
        ) : (
          <span className="inline-flex items-center gap-1 text-slate-400"><Globe className="w-3.5 h-3.5" /> No website on record</span>
        )}
        <StalenessPill staleness={b.staleness} testid={`business-stale-${b.business_key}`} />
      </div>

      {/* Score + stats */}
      <div className="mt-5 grid grid-cols-4 gap-3">
        <Stat label="Score" testid={`business-score-${b.business_key}`}>
          {ls?.health_score != null ? (
            <span className="flex items-baseline gap-1.5">
              <span className="font-heading text-2xl font-bold leading-none" style={{ color: scoreColor(ls.health_score) }}>{ls.health_score}</span>
              <Trend trend={trend} />
            </span>
          ) : <span className="text-slate-400 text-sm">—</span>}
          {ls?.health_score != null && <span className="text-[10px] text-slate-400">{scoreBand(ls.health_score)}</span>}
        </Stat>
        <Stat label="Opportunities">
          <span className="font-heading text-2xl font-bold text-slate-900 leading-none">{ls?.counts?.total ?? "—"}</span>
        </Stat>
        <Stat label="High priority">
          <span className="font-heading text-2xl font-bold leading-none text-red-600">{ls ? (ls.high_priority ?? 0) : "—"}</span>
        </Stat>
        <Stat label="Unresolved">
          <span className="font-heading text-2xl font-bold leading-none text-orange-600">{b.fix?.unresolved ?? 0}</span>
        </Stat>
      </div>

      <ScoreSparkline history={b.score_history} bizKey={b.business_key} />

      {/* Compliance + website + last-scan meta */}
      <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px]">
        {comp?.available && comp.band && (
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-semibold ${compBandCls(comp.band)}`}>
            {comp.band === "GOOD" ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
            Compliance: {COMPLIANCE_BAND[comp.band]?.label || comp.band_label || comp.band}
          </span>
        )}
        {comp?.alerts > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 text-amber-700 px-2 py-0.5 font-semibold">
            <AlertTriangle className="w-3 h-3" /> {comp.alerts} compliance alert{comp.alerts !== 1 ? "s" : ""}
          </span>
        )}
        {ls?.website_status && (
          <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 px-2 py-0.5 font-medium">
            <Globe className="w-3 h-3" /> Website: {websiteLabel(ls.website_status)}
          </span>
        )}
      </div>

      <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-400">
        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {ls?.at ? `Last scan ${new Date(ls.at).toLocaleDateString()}` : "Never scanned"}</span>
        {trend.previous != null && trend.current != null && (
          <span data-testid={`business-trend-${b.business_key}`} className="inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> {trend.previous} → {trend.current}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="mt-5 grid grid-cols-2 gap-2">
        <Button data-testid={`business-rescan-${b.business_key}`} onClick={onRescan}
          className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium active:scale-95 transition-transform">
          <ScanSearch className="w-4 h-4 mr-1.5" /> Re-scan
        </Button>
        <Button data-testid={`business-check-${b.business_key}`} onClick={onCheck} variant="outline"
          className="rounded-lg border-slate-200 hover:border-indigo-300 hover:text-indigo-700 font-medium">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Check for Changes
        </Button>
        <Button data-testid={`business-report-${b.business_key}`} onClick={onReport} variant="outline" disabled={!ls?.id}
          className="rounded-lg border-slate-200 hover:border-slate-300 font-medium">
          <FileText className="w-4 h-4 mr-1.5" /> View Report
        </Button>
        <Button data-testid={`business-history-${b.business_key}`} onClick={onHistory} variant="outline"
          className="rounded-lg border-slate-200 hover:border-slate-300 font-medium">
          <History className="w-4 h-4 mr-1.5" /> View History
        </Button>
      </div>
      <button onClick={onRemove} data-testid={`business-remove-${b.business_key}`}
        className="mt-3 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-600 transition-colors">
        <Trash2 className="w-3.5 h-3.5" /> Remove from My Businesses
      </button>
    </motion.div>
  );
}

function Stat({ label, children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid={testid}>
      <div className="flex flex-col gap-0.5">{children}</div>
      <div className="text-[10px] text-slate-500 mt-1.5">{label}</div>
    </div>
  );
}

function Trend({ trend }) {
  if (trend?.delta == null) return null;
  const up = trend.delta > 0, flat = trend.delta === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const cls = flat ? "text-slate-400" : up ? "text-emerald-600" : "text-red-600";
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${cls}`}>
      <Icon className="w-3.5 h-3.5" />{up ? `+${trend.delta}` : trend.delta}
    </span>
  );
}

// Only actual completed scans with real timestamps + a real score. Never fabricates history.
function chartData(history) {
  return (history || [])
    .filter((s) => s && s.score != null && s.at)
    .map((s) => ({ id: s.id, score: s.score, at: s.at, ts: new Date(s.at).getTime() }))
    .sort((a, b) => a.ts - b.ts);
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg bg-slate-900 text-white px-3 py-1.5 text-xs shadow-lg">
      <div className="font-bold">Score {p.score}</div>
      <div className="text-slate-300">{new Date(p.at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</div>
    </div>
  );
}

function ScoreSparkline({ history, bizKey }) {
  const data = chartData(history);
  if (data.length < 2) return null;
  const gid = `spark-${String(bizKey).replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <div className="mt-4" data-testid={`business-sparkline-${bizKey}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Score over time</span>
        <span className="text-[10px] text-slate-400">{data.length} scans</span>
      </div>
      <div className="h-12">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 2, right: 2, bottom: 0, left: 2 }}>
            <defs>
              <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#4f46e5" stopOpacity={0} />
              </linearGradient>
            </defs>
            <YAxis domain={[0, 100]} hide />
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#c7d2fe", strokeWidth: 1 }} />
            <Area type="monotone" dataKey="score" stroke="#4f46e5" strokeWidth={2}
              fill={`url(#${gid})`} dot={{ r: 2, fill: "#4f46e5" }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ScoreTrendChart({ history }) {
  const data = chartData(history);
  if (data.length < 2) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm text-slate-500" data-testid="score-trend-empty">
        {data.length === 1
          ? "A trend line appears once you have at least two completed scans."
          : "No completed scans with a score yet."}
      </div>
    );
  }
  return (
    <div className="h-52 -ml-2" data-testid="score-trend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <defs>
            <linearGradient id="trend-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#4f46e5" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="ts" type="number" scale="time" domain={["dataMin", "dataMax"]}
            tickFormatter={(t) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={32} />
          <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#c7d2fe", strokeWidth: 1 }} />
          <Area type="monotone" dataKey="score" stroke="#4f46e5" strokeWidth={2.5}
            fill="url(#trend-fill)" dot={{ r: 3, fill: "#4f46e5" }} activeDot={{ r: 5 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function ChangeDialog({ business, onClose, onResolved, onRediscover }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!business) { setData(null); return; }
    let active = true;
    setLoading(true); setData(null);
    api.post(`/saved-businesses/${encodeURIComponent(business.business_key)}/check-changes`, {})
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => { if (active) toast.error("Could not check for changes."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [business]);

  const resolve = async (action) => {
    if (!business) return;
    setActing(true);
    try {
      const { data: res } = await api.post(
        `/saved-businesses/${encodeURIComponent(business.business_key)}/resolve-change`,
        { action, changes: data?.changes || [], fresh_draft: data?.fresh_draft });
      toast.success(action === "confirm_new" ? "New information confirmed." : "Kept your existing information.");
      onResolved(res.saved);
      onClose();
    } catch (e) {
      toast.error("Could not save your choice. Please try again.");
    } finally { setActing(false); }
  };

  const changes = data?.changes || [];
  return (
    <Dialog open={!!business} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="change-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RefreshCw className="w-5 h-5 text-indigo-600" /> Check for changes</DialogTitle>
          <DialogDescription>{business?.name} — we re-checked public sources for updates to this business's identity.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 grid place-items-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mb-2" /> Checking public sources…</div>
        ) : !data ? (
          <p className="py-6 text-sm text-slate-500">No result. Please try again.</p>
        ) : (
          <div className="space-y-4">
            {!data.provider_configured && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600" data-testid="change-provider-note">
                No authoritative business-data provider is connected yet, so any suggestions below are <b>AI-assisted, not verified</b>. Connect a provider (e.g. Google Places) later to enable verified change monitoring for phone, address, hours and business status.
              </div>
            )}
            {changes.length === 0 ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-4 flex items-start gap-3" data-testid="change-none">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-emerald-900">No changes detected</p>
                  <p className="text-xs text-emerald-800/80 mt-0.5">Your saved information still matches what we can find. Last checked {new Date(data.checked_at).toLocaleString()}.</p>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-600">We found <b>{changes.length}</b> possible update{changes.length !== 1 ? "s" : ""}. Nothing is changed automatically — review and choose what to do.</p>
                <div className="space-y-3" data-testid="change-list">
                  {changes.map((c, i) => (
                    <div key={i} data-testid={`change-item-${c.field}`} className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-xs font-bold uppercase tracking-wide text-amber-800">
                          {c.kind === "new_information" ? "New information" : "Possible change detected"} · {c.label}
                        </span>
                        <ProvBadge status={c.discovered?.status} />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3 text-sm">
                        <div className="rounded-lg bg-white border border-slate-200 p-3">
                          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Previous</div>
                          <div className="text-slate-700 break-words">{valStr(c.previous?.value) || <span className="text-slate-400">Not on record</span>}</div>
                        </div>
                        <div className="rounded-lg bg-white border border-indigo-200 p-3">
                          <div className="text-[10px] uppercase tracking-wide text-indigo-500 font-semibold mb-1">Newly discovered</div>
                          <div className="text-slate-900 font-medium break-words">{valStr(c.discovered?.value)}</div>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                        {c.discovered?.source && <span>Source: {c.discovered.source}</span>}
                        {c.discovered?.checked_at && <span>· Checked {new Date(c.discovered.checked_at).toLocaleDateString()}</span>}
                        {c.discovered?.confidence != null && <span>· Confidence {Math.round(c.discovered.confidence * 100)}%</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              <Button data-testid="change-rediscover" variant="outline" disabled={acting}
                onClick={() => { onClose(); onRediscover(business); }}
                className="rounded-lg border-slate-200 hover:border-indigo-300 hover:text-indigo-700">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Re-discover
              </Button>
              {changes.length > 0 && (
                <Button data-testid="change-confirm-new" disabled={acting} onClick={() => resolve("confirm_new")}
                  className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white">
                  {acting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />} Confirm New Information
                </Button>
              )}
              <Button data-testid="change-keep-existing" variant="outline" disabled={acting} onClick={() => resolve("keep_existing")}
                className="rounded-lg border-slate-200 hover:border-slate-300">
                Keep Existing Information
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ business, onClose }) {
  const events = business?.identity_events || [];
  const history = business?.score_history || [];
  return (
    <Dialog open={!!business} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-indigo-600" /> Business history</DialogTitle>
          <DialogDescription>{business?.name} — score trend and identity audit trail. This record is never overwritten.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Score history</h4>
            <ScoreTrendChart history={history} />
            {history.length === 0 ? (
              <p className="text-sm text-slate-500">No completed scans yet.</p>
            ) : (
              <div className="space-y-1.5 mt-3" data-testid="history-scores">
                {history.map((s, i) => {
                  const prev = history[i + 1];
                  const delta = (s.score != null && prev?.score != null) ? s.score - prev.score : null;
                  return (
                    <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm">
                      <span className="text-slate-500">{s.at ? new Date(s.at).toLocaleString() : "—"}</span>
                      <span className="flex items-center gap-2">
                        <b className="font-heading" style={{ color: scoreColor(s.score) }}>{s.score ?? "—"}</b>
                        {delta != null && (
                          <span className={`text-xs font-bold ${delta > 0 ? "text-emerald-600" : delta < 0 ? "text-red-600" : "text-slate-400"}`}>
                            {delta > 0 ? `+${delta}` : delta}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Identity &amp; verification trail</h4>
            {events.length === 0 ? (
              <p className="text-sm text-slate-500">No identity events recorded yet.</p>
            ) : (
              <ol className="relative border-l border-slate-200 ml-2 space-y-4" data-testid="history-events">
                {[...events].reverse().map((e, i) => (
                  <li key={i} className="ml-4">
                    <span className="absolute -left-[7px] mt-1.5 w-3 h-3 rounded-full bg-indigo-500 border-2 border-white" />
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-800">{e.label}</p>
                      <ProvBadge status={e.status} />
                    </div>
                    <p className="text-[11px] text-slate-400">
                      {e.at ? new Date(e.at).toLocaleString() : ""}{e.source ? ` · ${e.source}` : ""}
                    </p>
                    {e.detail && <p className="text-xs text-slate-500 mt-0.5">{e.detail}</p>}
                    {e.fields?.length ? <p className="text-xs text-slate-500 mt-0.5">Fields: {e.fields.join(", ")}</p> : null}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function compBandCls(band) {
  if (band === "GOOD") return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (band === "CRITICAL") return "text-red-700 bg-red-50 border-red-200";
  return "text-amber-700 bg-amber-50 border-amber-200";
}

function websiteLabel(status) {
  const m = { VERIFIED: "Live", NONE: "None", ERROR: "Unreachable", UNKNOWN: "Unknown" };
  return m[status] || status;
}

function valStr(v) {
  if (v == null || v === "") return "";
  if (Array.isArray(v)) return v.join(" · ");
  if (typeof v === "object") {
    const lat = v.lat ?? v.latitude, lng = v.lng ?? v.longitude;
    return lat != null && lng != null ? `${lat}, ${lng}` : JSON.stringify(v);
  }
  return String(v);
}
